import React, { useState, useEffect } from 'react';
import { Dialog, Box, Typography, keyframes } from '@mui/material';

// Animación de barajado: las cartas rotan y se mueven como si se estuvieran mezclando
const shuffle1 = keyframes`
    0%, 100% {
        transform: translateX(0) translateY(0) rotate(-5deg);
        z-index: 3;
    }
    25% {
        transform: translateX(30px) translateY(-20px) rotate(15deg);
        z-index: 1;
    }
    50% {
        transform: translateX(0) translateY(-10px) rotate(-5deg);
        z-index: 2;
    }
    75% {
        transform: translateX(-30px) translateY(-20px) rotate(-25deg);
        z-index: 1;
    }
`;

const shuffle2 = keyframes`
    0%, 100% {
        transform: translateX(0) translateY(0) rotate(0deg);
        z-index: 2;
    }
    25% {
        transform: translateX(-25px) translateY(-15px) rotate(-20deg);
        z-index: 3;
    }
    50% {
        transform: translateX(0) translateY(-5px) rotate(0deg);
        z-index: 1;
    }
    75% {
        transform: translateX(25px) translateY(-15px) rotate(20deg);
        z-index: 3;
    }
`;

const shuffle3 = keyframes`
    0%, 100% {
        transform: translateX(0) translateY(0) rotate(5deg);
        z-index: 1;
    }
    25% {
        transform: translateX(-35px) translateY(-25px) rotate(-10deg);
        z-index: 2;
    }
    50% {
        transform: translateX(0) translateY(-15px) rotate(5deg);
        z-index: 3;
    }
    75% {
        transform: translateX(35px) translateY(-25px) rotate(25deg);
        z-index: 2;
    }
`;

// Animación de rebote vertical suave para el texto
const bounce = keyframes`
    0%, 100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-5px);
    }
`;

// Componente de carta con imagen del reverso
const CardBack = ({ delay, animationKeyframes, cardBackPath }) => (
    <Box
        sx={{
            width: 60,
            height: 84,
            borderRadius: 0,
            boxShadow: '3px 3px 10px rgba(0,0,0,0.4)',
            position: 'absolute',
            overflow: 'hidden',
            animation: `${animationKeyframes} 1.2s ease-in-out infinite`,
            animationDelay: `${delay}s`,
        }}
    >
        <Box
            component="img"
            src={cardBackPath}
            alt="Card back"
            sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
            }}
        />
    </Box>
);

const ShufflingModal = ({ open, playerName, playerAvatar }) => {
    // Get card back from localStorage
    const [cardBack, setCardBack] = useState('001');

    useEffect(() => {
        const savedCardBack = localStorage.getItem('cardBack') || '001';
        setCardBack(savedCardBack);
    }, [open]); // Re-check when modal opens

    const cardBackPath = `assets/images/deck/${cardBack}.png`;

    // Three cards with different animations
    const cards = [
        { animation: shuffle1, delay: 0 },
        { animation: shuffle2, delay: 0.1 },
        { animation: shuffle3, delay: 0.2 },
    ];

    return (
        <Dialog
            open={open}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #1a472a 0%, #2d5a3d 50%, #1a472a 100%)',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
                    overflow: 'visible',
                },
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backdropFilter: 'blur(8px)',
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 5,
                    px: 3,
                }}
            >
                {/* Contenedor de cartas animadas */}
                <Box
                    sx={{
                        position: 'relative',
                        width: 140,
                        height: 120,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                    }}
                >
                    {cards.map((card, index) => (
                        <CardBack
                            key={index}
                            animationKeyframes={card.animation}
                            delay={card.delay}
                            cardBackPath={cardBackPath}
                        />
                    ))}
                </Box>

                {/* Texto "Barajando..." */}
                <Typography
                    variant="h5"
                    sx={{
                        color: '#fff',
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                        animation: `${bounce} 1s ease-in-out infinite`,
                        mb: 1,
                    }}
                >
                    Barajando...
                </Typography>

                {/* Avatar y nombre del jugador */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    {playerAvatar && (
                        <Typography
                            sx={{
                                fontSize: 32,
                                lineHeight: 1,
                            }}
                        >
                            {playerAvatar}
                        </Typography>
                    )}
                    <Typography
                        variant="h6"
                        sx={{
                            color: '#ffd700',
                            fontWeight: 500,
                            textShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                        }}
                    >
                        {playerName}
                    </Typography>
                </Box>
            </Box>
        </Dialog>
    );
};

export default ShufflingModal;
